import type { FC } from 'react';

import { useMouseMovementOutcome } from '@packages/abtesting';
import { useConfig } from '@packages/utilities';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import type { ProductListSeoFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductListSeoFragmentFragmentDoc';
import { SearchResultPageDefaultContentFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchResultPageDefaultContentFragmentFragmentDoc';
import type { CmsDataJsonApi } from '@packages/cms-components/interfaces';

import { useOriginalSearchQuery } from '../../hooks/useOriginalSearchQuery';
import { ProductListPageLayout } from '../ProductListPageLayout';
import { SearchResultHeader } from '../SearchResultHeader';
import { SearchFeedback } from '../SearchFeedback';
import { ProductListSeo } from '../ProductListSeo';
import { Recommendations } from '../Recommendations';
import { SearchResultCategoryFilter } from '../SearchResultCategoryFilter';
import { CmsTopBanner } from '../CmsTopBanner';
import { useFeatureFlag } from '../../utils/featureFlags/useFeatureFlag';
import { inspiringSearchPrototype } from '../../activeFeatureFlags';
import { InspiringSearchRecommendationsDynamicWrapper } from '../InspiringSearchRecommendations';

/* GraphQL */ `
  fragment SearchResultPageDefaultContentFragment on SuccessfulSearchResult {
    ...SearchResultHeaderFragment
    ...ProductListPageLayoutFragment
    ...SearchResultCategoryFilterFragment
  }
`;

export type SearchResultPageDefaultContentProps = {
  maskedData: FragmentType<typeof SearchResultPageDefaultContentFragmentFragmentDoc>;
  topLevelCategories?: string[];
  maskedSeoResponse?: FragmentType<typeof ProductListSeoFragmentFragmentDoc>;
  bannerData?: CmsDataJsonApi;
  referrer?: string;
};

/**
 * SearchResultPageDefaultContent component description displayed in storybook
 * */
export const SearchResultPageDefaultContent: FC<SearchResultPageDefaultContentProps> = ({
  maskedData,
  topLevelCategories,
  maskedSeoResponse,
  bannerData,
  referrer,
}) => {
  useMouseMovementOutcome('2024_04_SoFiOn');

  const { feedback } = useConfig();
  const originalQuery = useOriginalSearchQuery();

  const data = getFragmentData(SearchResultPageDefaultContentFragmentFragmentDoc, maskedData);

  const hasInspiringSearchPrototype = useFeatureFlag(inspiringSearchPrototype);

  return (
    <ProductListPageLayout
      maskedData={data}
      topLevelCategories={topLevelCategories}
      referrer={referrer}
      aboveMainContentComponent={
        <>
          {bannerData && <CmsTopBanner bannerData={bannerData} />}
          <SearchResultHeader maskedData={data} originalQuery={originalQuery} />
        </>
      }
      belowProductsComponent={
        <>
          {hasInspiringSearchPrototype && <InspiringSearchRecommendationsDynamicWrapper />}
          <Recommendations />
          {feedback.enabled && <SearchFeedback />}
          {maskedSeoResponse && <ProductListSeo maskedData={maskedSeoResponse} />}
        </>
      }
      leftSidebarDesktopComponent={<SearchResultCategoryFilter maskedData={data} />}
      renderCategoryDialogContent={(closeDialog) => (
        <SearchResultCategoryFilter maskedData={data} onNavigate={closeDialog} />
      )}
    />
  );
};
